import React, { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLang } from "@/contexts/AppContexts";
import { Copy, Search } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  CartesianGrid,
} from "recharts";

const ReportTabs = ({ result }) => {
  const { t } = useLang();
  const gemini = result?.gemini || {};
  const metadata = result?.metadata || {};
  const ela = result?.ela || {};
  const noise = result?.noise || {};
  const aggregated = result?.aggregated || {};

  const histogramData = useMemo(
    () =>
      (ela.histogram || []).map((v, i) => ({
        bin: i * 16,
        count: v,
      })),
    [ela.histogram]
  );

  return (
    <Card className="p-1">
      <Tabs defaultValue="vision" className="w-full" data-testid="report-tabs">
        <TabsList className="w-full justify-start gap-1 bg-transparent p-1">
          <TabsTrigger value="vision" data-testid="report-tab-ai-vision">
            {t("tabs.vision")}
          </TabsTrigger>
          <TabsTrigger value="metadata" data-testid="report-tab-metadata">
            {t("tabs.metadata")}
          </TabsTrigger>
          <TabsTrigger value="forensics" data-testid="report-tab-pixel-forensics">
            {t("tabs.forensics")}
          </TabsTrigger>
          <TabsTrigger value="breakdown" data-testid="report-tab-breakdown">
            {t("tabs.breakdown")}
          </TabsTrigger>
        </TabsList>

        {/* AI Vision Tab */}
        <TabsContent value="vision" className="p-4 space-y-4">
          <div>
            <h3 className="font-display text-sm font-semibold mb-2">
              {t("vision.heading")}
            </h3>
            <p
              className="text-sm leading-relaxed text-foreground/90"
              data-testid="vision-reasoning"
            >
              {gemini.global_reasoning || t("vision.no_reasoning")}
            </p>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 font-mono-plex">
              {t("vision.key_indicators")}
            </h4>
            {gemini.key_indicators && gemini.key_indicators.length > 0 ? (
              <ul className="space-y-1.5" data-testid="vision-indicators">
                {gemini.key_indicators.map((k, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-foreground/90"
                  >
                    <span className="mt-1.5 inline-block h-1 w-1 shrink-0 rounded-full bg-primary" />
                    {k}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("vision.no_indicators")}
              </p>
            )}
          </div>
        </TabsContent>

        {/* Metadata Tab */}
        <TabsContent value="metadata" className="p-4 space-y-4">
          <MetadataPanel metadata={metadata} />
        </TabsContent>

        {/* Forensics Tab */}
        <TabsContent value="forensics" className="p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <KPI label={t("forensics.anomaly_score")} value={ela.anomaly_score?.toFixed?.(1) ?? ela.anomaly_score} highlight />
            <KPI label={t("forensics.mean_diff")} value={ela.mean_diff} />
            <KPI label={t("forensics.std_diff")} value={ela.std_diff} />
            <KPI label={t("forensics.max_diff")} value={ela.max_diff} />
            <KPI label={t("forensics.quad_var")} value={ela.quadrant_variance} />
            <KPI label={t("forensics.laplacian")} value={noise.laplacian_variance} />
            <KPI label={t("forensics.noise_mean")} value={noise.noise_mean} />
            <KPI label={t("forensics.noise_std")} value={noise.noise_std} />
            <KPI label={t("forensics.hf_ratio")} value={noise.high_freq_ratio} />
            <KPI label={t("forensics.edge_density")} value={noise.edge_density} />
          </div>

          {histogramData.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 font-mono-plex">
                {t("forensics.histogram_title")}
              </h4>
              <div
                className="h-44 w-full rounded-xl border bg-card p-2"
                data-testid="ela-histogram"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={histogramData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="bin" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <ChartTooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "0.5rem",
                        fontSize: "12px",
                      }}
                    />
                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Breakdown Tab */}
        <TabsContent value="breakdown" className="p-4 space-y-4">
          <BreakdownPanel aggregated={aggregated} />
        </TabsContent>
      </Tabs>
    </Card>
  );
};

const KPI = ({ label, value, highlight }) => (
  <div
    className={`rounded-xl border bg-card p-3 ${
      highlight ? "border-primary/30 bg-primary/5" : ""
    }`}
  >
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono-plex">
      {label}
    </p>
    <p className="font-display text-xl font-semibold tabular-nums mt-0.5">
      {value != null && value !== "" ? value : "—"}
    </p>
  </div>
);

const C2PATreeNode = ({ name, value, depth = 0 }) => {
  const isObject = value !== null && typeof value === "object";
  const entries = isObject
    ? Array.isArray(value)
      ? value.map((item, index) => [`[${index}]`, item])
      : Object.entries(value)
    : [];

  if (!isObject) {
    return (
      <div className="flex gap-2 py-0.5 text-xs font-mono-plex">
        {name && <span className="text-muted-foreground">{name}:</span>}
        <span className="break-all text-foreground/90">{String(value)}</span>
      </div>
    );
  }

  return (
    <details className="group py-0.5" open={depth < 2}>
      <summary className="cursor-pointer select-none text-xs font-mono-plex text-foreground/90">
        <span className="text-muted-foreground">{name}</span>{" "}
        <span className="text-muted-foreground/70">
          {Array.isArray(value) ? `[${entries.length}]` : `{${entries.length}}`}
        </span>
      </summary>
      <div className="ml-4 border-l pl-3">
        {entries.map(([childName, childValue]) => (
          <C2PATreeNode
            key={childName}
            name={childName}
            value={childValue}
            depth={depth + 1}
          />
        ))}
      </div>
    </details>
  );
};

const MetadataPanel = ({ metadata }) => {
  const { t } = useLang();
  const [search, setSearch] = useState("");

  const exif = useMemo(() => metadata.exif || {}, [metadata.exif]);
  const c2pa = metadata.c2pa || {};
  const c2paSummary = c2pa.summary || {};
  const c2paRaw = c2pa.raw;
  const entries = useMemo(
    () =>
      Object.entries(exif).filter(
        ([k, v]) =>
          !search ||
          k.toLowerCase().includes(search.toLowerCase()) ||
          String(v).toLowerCase().includes(search.toLowerCase())
      ),
    [exif, search]
  );

  const summary = [
    { key: t("metadata.format"), value: metadata.format },
    {
      key: t("metadata.dimensions"),
      value: metadata.size ? `${metadata.size[0]} × ${metadata.size[1]}` : null,
    },
    {
      key: t("metadata.file_size"),
      value: metadata.file_size_bytes
        ? `${(metadata.file_size_bytes / 1024).toFixed(1)} KB`
        : null,
    },
    {
      key: t("metadata.camera"),
      value:
        [metadata.make, metadata.model].filter(Boolean).join(" ") || null,
    },
    { key: t("metadata.software"), value: metadata.software },
    { key: t("metadata.datetime"), value: metadata.datetime_original },
    {
      key: t("metadata.ai_signature"),
      value: metadata.ai_signature_detected
        ? `${t("metadata.detected")}: ${(metadata.ai_signature_evidence || []).join(", ")}`
        : t("metadata.not_detected"),
      important: metadata.ai_signature_detected,
    },
    {
      key: t("metadata.c2pa"),
      value: metadata.has_c2pa ? t("metadata.detected") : t("metadata.not_detected"),
      important: metadata.has_c2pa,
    },
  ];

  const c2paSummaryRows = [
    { key: t("metadata.signed_by"), value: c2paSummary.signed_by },
    { key: t("metadata.issued_date"), value: c2paSummary.issued_date },
    { key: t("metadata.algorithm"), value: c2paSummary.algorithm },
    { key: t("metadata.cert_status"), value: c2paSummary.cert_status },
    { key: t("metadata.software"), value: c2paSummary.software },
  ];

  // Extra C2PA detail rows — now much richer
  const c2paExtraRows = [
    { key: "Claim Generator", value: c2paSummary.claim_generator },
    { key: "Issuer (Organization)", value: c2paSummary.issuer },
    { key: "Certificate Serial", value: c2paSummary.cert_serial_number },
    { key: "Validation State", value: c2paSummary.validation_state },
    { key: "Document Title", value: c2paSummary.title },
    { key: "C2PA Version", value: c2paSummary.claim_version },
    { key: "C2PA Spec", value: c2paSummary.spec_version },
    { key: "Library Version", value: c2paSummary.library_version ? `c2pa-rs/${c2paSummary.library_version}` : null },
    { key: "Software Version", value: c2paSummary.software_version },
    { key: "Creation Date", value: c2paSummary.creation_date },
    { key: "Action", value: c2paSummary.action_type },
    { key: "Digital Source Type", value: c2paSummary.digital_source_type_display || c2paSummary.digital_source_type },
    { key: "Instance ID", value: c2paSummary.instance_id },
    { key: "Manifest ID", value: c2paSummary.manifest_label },
  ].filter((r) => r.value);

  // Validation details (c2paviewer.com style)
  const validationDetails = c2paSummary.validation_details || [];
  const validationSuccesses = validationDetails.filter((v) => v.status === "success");
  const validationFailures = validationDetails.filter((v) => v.status === "failure");
  const validationInfo = validationDetails.filter((v) => v.status === "informational");

  // AI Generator detection from C2PA
  const aiGenerator = c2paSummary.ai_generator || {};

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {summary.map((s) => (
          <div
            key={s.key}
            className={`rounded-lg border bg-card p-2.5 ${
              s.important ? "border-amber-400/40 bg-amber-50/50 dark:bg-amber-950/20" : ""
            }`}
          >
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono-plex">
              {s.key}
            </p>
            <p className="text-sm font-medium font-mono-plex break-words">
              {s.value || <span className="text-muted-foreground">{t("metadata.not_present")}</span>}
            </p>
          </div>
        ))}
      </div>

      {/* Highlights */}
      {metadata.highlights && metadata.highlights.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 font-mono-plex">
            {t("metadata.highlights")}
          </h4>
          <div className="flex flex-wrap gap-2" data-testid="metadata-highlights">
            {metadata.highlights.map((h, i) => (
              <Badge
                key={i}
                variant="outline"
                className="border-amber-400/40 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900"
              >
                <span className="font-mono-plex">
                  {h.key}: {h.value}
                </span>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* C2PA manifest */}
      {metadata.has_c2pa && (
        <div className="space-y-3" data-testid="c2pa-manifest-panel">
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 font-mono-plex">
              {t("metadata.c2pa_manifest")}
            </h4>
            {c2pa.parse_error && !c2paRaw && (
              <p className="rounded-lg border border-amber-400/40 bg-amber-50/50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/20 dark:text-amber-100">
                {t("metadata.c2pa_parse_error")}: {c2pa.parse_error}
              </p>
            )}
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2"
            data-testid="c2pa-summary"
          >
            {c2paSummaryRows.map((row) => (
              <div key={row.key} className="rounded-lg border bg-card p-2.5">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono-plex">
                  {row.key}
                </p>
                <p className="text-sm font-medium font-mono-plex break-words">
                  {row.value || (
                    <span className="text-muted-foreground">
                      {t("metadata.not_present")}
                    </span>
                  )}
                </p>
              </div>
            ))}
          </div>

          {/* C2PA AI Generator Alert */}
          {aiGenerator.detected && (
            <div className="rounded-lg border-2 border-red-500 bg-red-50/80 dark:bg-red-950/30 p-4" data-testid="c2pa-ai-alert">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">🤖</span>
                <h4 className="text-sm font-bold text-red-700 dark:text-red-300 uppercase tracking-wider">
                  DEFINITIVE: AI Generated Image — Confirmed via C2PA
                </h4>
              </div>
              <p className="text-sm text-red-800 dark:text-red-200 mb-2">
                The C2PA content credentials definitively identify this image as generated by: <strong>{aiGenerator.model_name}</strong>
              </p>
              {aiGenerator.all_matches && aiGenerator.all_matches.length > 1 && (
                <p className="text-xs text-red-600 dark:text-red-400 mb-2">
                  All matched patterns: <strong>{aiGenerator.all_matches.join(", ")}</strong>
                </p>
              )}
              {aiGenerator.evidence && aiGenerator.evidence.length > 0 && (
                <div className="text-xs text-red-700 dark:text-red-300 space-y-0.5">
                  {aiGenerator.evidence.slice(0, 10).map((ev, i) => (
                    <p key={i} className="font-mono-plex">
                      {typeof ev === "string" ? (
                        <>• {ev}</>
                      ) : (
                        <>• <span className="text-red-500 dark:text-red-400">{ev.field}</span>{": \""}{ev.value}{"\""} <span className="text-red-500/70 dark:text-red-400/70">[matched: {ev.pattern}]</span></>
                      )}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* C2PA Validation Results (c2paviewer.com style) */}
          {validationDetails.length > 0 && (
            <div className="rounded-lg border bg-card p-3 space-y-3" data-testid="c2pa-validation-results">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-mono-plex">
                Validation Results
              </h4>
              {validationFailures.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-red-600 dark:text-red-400 mb-1.5">❌ Failures ({validationFailures.length})</p>
                  <div className="space-y-1">
                    {validationFailures.map((v, i) => (
                      <div key={i} className="text-xs font-mono-plex bg-red-50/50 dark:bg-red-950/20 rounded px-2 py-1">
                        <span className="text-red-600 dark:text-red-400">{v.code}</span>
                        {v.explanation && <span className="text-muted-foreground ml-2">— {v.explanation}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {validationInfo.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-yellow-600 dark:text-yellow-400 mb-1.5">ℹ️ Informational ({validationInfo.length})</p>
                  <div className="space-y-1">
                    {validationInfo.map((v, i) => (
                      <div key={i} className="text-xs font-mono-plex bg-yellow-50/50 dark:bg-yellow-950/20 rounded px-2 py-1">
                        <span className="text-yellow-700 dark:text-yellow-400">{v.code}</span>
                        {v.explanation && <span className="text-muted-foreground ml-2">— {v.explanation}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {validationSuccesses.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-green-600 dark:text-green-400 mb-1.5">✅ Passed ({validationSuccesses.length})</p>
                  <div className="space-y-0.5">
                    {validationSuccesses.map((v, i) => (
                      <div key={i} className="text-xs font-mono-plex text-muted-foreground">
                        <span className="text-green-600 dark:text-green-400">✓</span> {v.code}
                        {v.explanation && <span className="ml-1">— {v.explanation}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Extra C2PA detail rows */}
          {c2paExtraRows.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {c2paExtraRows.map((row) => (
                <div key={row.key} className="rounded-lg border bg-card p-2.5">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono-plex">
                    {row.key}
                  </p>
                  <p className="text-sm font-medium font-mono-plex break-words">
                    {row.value}
                  </p>
                </div>
              ))}
            </div>
          )}

          {c2paRaw ? (
            <Tabs defaultValue="tree" className="rounded-xl border bg-card p-2">
              <TabsList className="mb-2">
                <TabsTrigger value="tree">{t("metadata.tree")}</TabsTrigger>
                <TabsTrigger value="raw">{t("metadata.raw_json")}</TabsTrigger>
              </TabsList>
              <TabsContent value="tree" className="m-0">
                <div
                  className="max-h-[800px] overflow-y-auto rounded-lg bg-muted/30 p-3"
                  data-testid="c2pa-tree"
                >
                  <C2PATreeNode name="c2pa" value={c2paRaw} />
                </div>
              </TabsContent>
              <TabsContent value="raw" className="m-0">
                <div
                  className="max-h-[800px] overflow-y-auto rounded-lg bg-muted/30 p-3"
                  data-testid="c2pa-raw-json"
                >
                  <pre className="whitespace-pre-wrap break-words text-xs font-mono-plex text-foreground/90">
                    {JSON.stringify(c2paRaw, null, 2)}
                  </pre>
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            !c2pa.parse_error && (
              <p className="rounded-lg border bg-card px-3 py-2 text-sm text-muted-foreground">
                {t("metadata.c2pa_no_manifest")}
              </p>
            )
          )}
        </div>
      )}

      {/* Search + table */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("metadata.search_placeholder")}
            className="w-full rounded-lg border bg-card pl-9 pr-3 py-1.5 text-sm placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="metadata-search-input"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(JSON.stringify(metadata, null, 2));
              toast.success(t("metadata.copied"));
            } catch (err) {
              console.error("Clipboard error:", err);
              toast.error(t("errors.generic") || "Failed to copy");
            }
          }}
          data-testid="metadata-copy-button"
        >
          <Copy className="h-3.5 w-3.5 mr-1.5" />
          {t("metadata.copy")}
        </Button>
      </div>
      <ScrollArea className="max-h-[800px] rounded-xl border bg-card">
        {entries.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            {t("metadata.no_exif")}
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="metadata-table">
            <thead className="sticky top-0 bg-muted/60 backdrop-blur">
              <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2 text-left font-medium">
                  {t("metadata.key")}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t("metadata.value")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {entries.map(([k, v]) => {
                const isSuspicious = (metadata.suspicious_fields || []).includes(k);
                return (
                  <tr
                    key={k}
                    className={
                      isSuspicious
                        ? "bg-amber-50/60 dark:bg-amber-950/20"
                        : ""
                    }
                  >
                    <td className="px-3 py-2 font-mono-plex text-xs text-muted-foreground align-top">
                      {k}
                    </td>
                    <td className="px-3 py-2 font-mono-plex text-xs break-all">
                      {String(v)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </ScrollArea>
    </div>
  );
};

const BreakdownPanel = ({ aggregated }) => {
  const { t } = useLang();
  const breakdown = aggregated.breakdown || [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-3">
          <p className="text-[11px] uppercase tracking-wider text-red-700 dark:text-red-300 font-mono-plex">
            {t("breakdown.ai_score")}
          </p>
          <p
            className="font-display text-2xl font-bold tabular-nums text-red-700 dark:text-red-300"
            data-testid="breakdown-ai-score"
          >
            {aggregated.ai_score?.toFixed?.(1) ?? aggregated.ai_score ?? 0}
          </p>
        </div>
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
          <p className="text-[11px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 font-mono-plex">
            {t("breakdown.real_score")}
          </p>
          <p
            className="font-display text-2xl font-bold tabular-nums text-emerald-700 dark:text-emerald-300"
            data-testid="breakdown-real-score"
          >
            {aggregated.real_score?.toFixed?.(1) ?? aggregated.real_score ?? 0}
          </p>
        </div>
      </div>
      <div
        className="rounded-xl border bg-card overflow-hidden"
        data-testid="breakdown-table"
      >
        {breakdown.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            {t("breakdown.no_signals")}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/60">
              <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2 text-left font-medium">
                  {t("breakdown.signal")}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t("breakdown.impact")}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("breakdown.weight")}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t("breakdown.detail")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {breakdown.map((row, i) => (
                <tr key={i}>
                  <td className="px-3 py-2 font-mono-plex text-xs text-muted-foreground">
                    {row.signal}
                  </td>
                  <td className="px-3 py-2">
                    <Badge
                      variant="outline"
                      className={
                        row.impact === "AI"
                          ? "text-red-700 bg-red-50 border-red-200 dark:text-red-200 dark:bg-red-950/30 dark:border-red-900"
                          : row.impact === "REAL"
                          ? "text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-900"
                          : ""
                      }
                    >
                      {row.impact}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right font-mono-plex tabular-nums">
                    {row.weight}
                  </td>
                  <td className="px-3 py-2 text-xs text-foreground/85">
                    {row.detail}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export { ReportTabs };
export default ReportTabs;
